// src/pages/fiscal/Dashboard/DashboardNotas.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Typography, useTheme, alpha, Card, CardContent, ButtonGroup, Button, Chip, Pagination,
  useMediaQuery,
  Container,
  Popover,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart, Area, Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';

import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import { useGraficoResponsivo } from '../../../shared/components/graficos/useGraficoResponsivo';
import {
  GraficosNotasService,
  IDashboardNotasGraficos,
  IGraficoNotasItem,
  ITopProdutoItem,
  TPeriodoNotas,
} from '../../../shared/services/api/Fiscal/GraficosNotasService';

dayjs.locale('pt-br');
dayjs.extend(utc);

type TModoExibicao = 'detalhado' | 'total' | 'compravenda';
type TModoRosca = 'compravenda' | 'notas';

interface PontoNotas extends IGraficoNotasItem {
  label: string;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatQuantity = (value: number) => {
  return value.toLocaleString('pt-BR');
};

interface ICustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: PontoNotas }>;
  label?: string;
}

const CustomTooltip: React.FC<ICustomTooltipProps> = ({ active, payload, label }) => {
  const theme = useTheme();

  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload as PontoNotas;

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        boxShadow: theme.shadows[4],
        minWidth: 190,
      }}
    >
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.primary.dark }}>
          NF-e
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valornfe)} ({item.qtdenfe})
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.warning.dark }}>
          NFC-e
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valornfce)} ({item.qtdenfce})
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        mt={0.5}
        pt={0.5}
        borderTop={`1px solid ${alpha(theme.palette.divider, 0.2)}`}
      >
        <Typography variant="caption" sx={{ color: theme.palette.success.dark }} fontWeight="bold">
          Venda (NF)
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valortotal)} ({item.qtdetotal})
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.info.dark }}>
          Compra (NF)
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(item.valorcompra)} ({item.qtdecompra})
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        mt={0.5}
        pt={0.5}
        borderTop={`1px solid ${alpha(theme.palette.divider, 0.2)}`}
      >
        <Typography variant="caption" fontWeight="bold">
          Saldo
        </Typography>
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{ color: item.saldo >= 0 ? theme.palette.success.dark : theme.palette.error.dark }}
        >
          {formatCurrency(item.saldo)}
        </Typography>
      </Box>
    </Paper>
  );
};

interface IRankingProdutosProps {
  titulo: string;
  icon: React.ReactNode;
  cor: string;
  itens: ITopProdutoItem[];
  metrica: 'quantidade' | 'valor';
}

const ITENS_POR_PAGINA = 5;

const RankingProdutos: React.FC<IRankingProdutosProps> = ({ titulo, icon, cor, itens, metrica }) => {
  const theme = useTheme();
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setPagina(1);
  }, [itens]);

  const valorPrincipal = (item: ITopProdutoItem) => (metrica === 'valor' ? item.valor : item.quantidade);
  const maiorValor = itens[0] ? valorPrincipal(itens[0]) : 1;

  const totalPaginas = Math.max(1, Math.ceil(itens.length / ITENS_POR_PAGINA));
  const itensPagina = itens.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA);

  return (
    <Paper
      sx={{
        p: 2,
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        pb={1}
        borderBottom={`2px solid ${alpha(cor, 0.2)}`}
      >
        {icon}

        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ flex: 1 }}
        >
          {titulo}
        </Typography>

        <Chip
          label={`${itens.length} produtos`}
          size="small"
          sx={{ bgcolor: alpha(cor, 0.12), color: cor, fontWeight: 'bold' }}
          variant="outlined"
        />
      </Box>

      {itens.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Nenhum produto encontrado
          </Typography>
        </Box>
      )}

      {itensPagina.map((item, index) => (
        <Box
          key={item.idproduto}
          sx={{
            p: 1.5,
            borderRadius: 2,
            mb: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.4),
            transition: 'all 0.2s',
            cursor: 'default',
            '&:hover': {
              bgcolor: alpha(cor, 0.08),
              transform: 'translateX(4px)',
            },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5} flex={1} sx={{ minWidth: 0 }}>
              <Chip
                label={`#${(pagina - 1) * ITENS_POR_PAGINA + index + 1}`}
                size="small"
                sx={{ fontWeight: 'bold', minWidth: 40, bgcolor: cor, color: '#fff' }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  noWrap
                  title={item.descricao ?? undefined}
                >
                  {item.descricao || 'Produto sem descrição'}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {item.ean || 'Sem EAN'}
                </Typography>
              </Box>
            </Box>

            <Box textAlign="right" sx={{ flexShrink: 0, pl: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: cor }}>
                {metrica === 'valor' ? formatCurrency(item.valor) : `${formatQuantity(item.quantidade)} un.`}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {metrica === 'valor' ? `${formatQuantity(item.quantidade)} un.` : formatCurrency(item.valor)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1,
              height: 3,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.grey[500], 0.2),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${(valorPrincipal(item) / (maiorValor || 1)) * 100}%`,
                height: '100%',
                bgcolor: cor,
                borderRadius: 1,
                transition: 'width 0.3s',
              }}
            />
          </Box>
        </Box>
      ))}

      {itens.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt="auto"
          pt={1}
          borderTop={`1px solid ${alpha(theme.palette.divider, 0.1)}`}
        >
          <Pagination
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            count={totalPaginas}
            size="small"
            color="primary"
          />
        </Box>
      )}
    </Paper>
  );
};

export const DashboardNotas: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const graficoResp = useGraficoResponsivo();

  const [isLoading, setIsLoading] = useState(false);
  const [msgType, setMsgType] = useState<'success' | 'error' | 'warning' | 'info'>('error');
  const [msg, setMsg] = useState('');
  const [openMsg, setOpenMsg] = useState(false);

  const [periodo, setPeriodo] = useState<TPeriodoNotas>('atual');
  const [modoExibicao, setModoExibicao] = useState<TModoExibicao>('detalhado');
  const [modoRosca, setModoRosca] = useState<TModoRosca>('compravenda');
  const [ano, setAno] = useState<number>(dayjs().year());
  const [mes, setMes] = useState<number>(dayjs().month() + 1);

  const [anchorPeriodo, setAnchorPeriodo] = useState<HTMLElement | null>(null);
  const [anoPopover, setAnoPopover] = useState(dayjs().year());
  const [mesPopover, setMesPopover] = useState(dayjs().month() + 1);

  const [dados, setDados] = useState<IDashboardNotasGraficos | null>(null);
  const [expandirNfe, setExpandirNfe] = useState(false);

  const nomesMeses = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const nome = dayjs().month(i).format('MMM').replace('.', '');
    return nome.charAt(0).toUpperCase() + nome.slice(1);
  }), []);

  const abrirSeletorPeriodo = (target: HTMLElement) => {
    if (periodo === 'atual') return;

    setAnoPopover(ano);
    setMesPopover(mes);
    setAnchorPeriodo(target);
  };

  useEffect(() => {
    const carregarDashboard = async () => {
      setIsLoading(true);

      const result = await GraficosNotasService.getDashboardNotasGraficos(
        periodo,
        periodo !== 'atual' ? ano : undefined,
        periodo === 'mensal' ? mes : undefined,
      );

      setIsLoading(false);

      if (result instanceof Error) {
        setMsgType('error');
        setMsg(result.message);
        setOpenMsg(true);
      } else {
        setDados(result);
      }
    };

    carregarDashboard();
  }, [periodo, ano, mes]);

  const totais = dados?.totais ?? {
    valornfe: 0,
    qtdenfe: 0,
    valornferemessa: 0,
    qtdenferemessa: 0,
    valornfedevolucao: 0,
    qtdenfedevolucao: 0,
    valornfecancelada: 0,
    qtdenfecancelada: 0,
    valornfce: 0,
    qtdenfce: 0,
    valortotal: 0,
    qtdetotal: 0,
    valorcompra: 0,
    qtdecompra: 0,
    saldo: 0,
  };

  const impostos = dados?.impostos ?? { icms: 0, icmsst: 0, ipi: 0, pis: 0, cofins: 0, total: 0, desconto: 0 };

  const intervaloFormatado = useMemo(() => {
    if (!dados?.intervalo) return '';

    return `${dayjs(dados.intervalo.datainicial).format('DD/MM/YY')} — ${dayjs(dados.intervalo.datafinal).format('DD/MM/YY')}`;
  }, [dados?.intervalo]);

  const chartData = useMemo<PontoNotas[]>(() => {
    const agrupamento = dados?.agrupamento ?? (periodo === 'anual' ? 'mes' : 'dia');

    return (dados?.grafico ?? []).map((item) => ({
      ...item,
      label: agrupamento === 'dia'
        ? dayjs.utc(item.data).format('DD/MM')
        : dayjs.utc(item.data).format('MMM/YY'),
    }));
  }, [dados?.grafico, dados?.agrupamento, periodo]);

  const dadosRosca = useMemo(() => {
    if (modoRosca === 'compravenda') {
      return [
        { name: 'Venda (NF)', value: totais.qtdetotal, valor: totais.valortotal, color: theme.palette.success.dark },
        { name: 'Compra (NF)', value: totais.qtdecompra, valor: totais.valorcompra, color: theme.palette.info.dark },
      ];
    }

    return [
      { name: 'NF-e', value: totais.qtdenfe, valor: totais.valornfe, color: theme.palette.primary.dark },
      { name: 'NFC-e', value: totais.qtdenfce, valor: totais.valornfce, color: theme.palette.warning.dark },
    ];
  }, [modoRosca, totais.qtdenfe, totais.qtdenfce, totais.valornfe, totais.valornfce, totais.qtdetotal, totais.valortotal, totais.qtdecompra, totais.valorcompra, theme]);

  const totalValorRosca = dadosRosca.reduce((acc, item) => acc + item.valor, 0);

  return (
    <LayoutBaseDeDashboard
      barraDeFerramentas={<Box />}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: 'transparent',
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          {/* CARDS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              mb: 2,
            }}
          >
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                onClick={(e) => abrirSeletorPeriodo(e.currentTarget)}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  cursor: periodo !== 'atual' ? 'pointer' : 'default',
                  opacity: periodo !== 'atual' ? 1 : 0.75,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': periodo !== 'atual' ? {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  } : undefined,
                }}
              >
                <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Período
                      </Typography>

                      <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {periodo === 'atual'
                            ? 'Mês Atual'
                            : periodo === 'mensal'
                              ? `${nomesMeses[mes - 1]}/${ano}`
                              : ano}
                        </Typography>

                        {periodo !== 'atual' && (
                          <ArrowDropDownIcon
                            sx={{ color: theme.palette.info.dark }}
                          />
                        )}
                      </Box>

                      {intervaloFormatado && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {intervaloFormatado}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <CalendarTodayIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.info.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Popover
                open={Boolean(anchorPeriodo)}
                anchorEl={anchorPeriodo}
                onClose={() => setAnchorPeriodo(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box sx={{ width: periodo === 'mensal' ? 260 : 220, p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((valor) => valor - 1)}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="subtitle2" fontWeight="bold">
                      {anoPopover}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((valor) => valor + 1)}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {periodo === 'mensal' && (
                    <Grid container spacing={0.5} sx={{ mb: 1.5 }}>
                      {nomesMeses.map((nome, index) => {
                        const numeroMes = index + 1;

                        return (
                          <Grid item xs={4} key={numeroMes}>
                            <Button
                              fullWidth
                              size="small"
                              variant={mesPopover === numeroMes ? 'contained' : 'outlined'}
                              onClick={() => setMesPopover(numeroMes)}
                              sx={{ minWidth: 0, px: 0.5 }}
                            >
                              {nome}
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}

                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setAno(anoPopover);
                      if (periodo === 'mensal') setMes(mesPopover);
                      setAnchorPeriodo(null);
                    }}
                  >
                    {periodo === 'mensal'
                      ? `Filtrar ${nomesMeses[mesPopover - 1]}/${anoPopover}`
                      : `Filtrar ${anoPopover}`}
                  </Button>
                </Box>
              </Popover>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                onClick={() => setExpandirNfe((valor) => !valor)}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total NF-e
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.primary.dark }}
                      >
                        {formatCurrency(totais.valornfe)}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={0.25}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {totais.qtdenfe} notas
                          {' · '}
                          <Typography component="span" variant="caption" sx={{ color: theme.palette.secondary.main }}>
                            Rem {totais.qtdenferemessa}
                          </Typography>
                          {' · '}
                          <Typography component="span" variant="caption" sx={{ color: theme.palette.warning.dark }}>
                            Dev {totais.qtdenfedevolucao}
                          </Typography>
                          {' · '}
                          <Typography component="span" variant="caption" sx={{ color: theme.palette.error.dark }}>
                            Canc {totais.qtdenfecancelada}
                          </Typography>
                        </Typography>

                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{
                            color: theme.palette.text.secondary,
                            transition: 'transform 0.2s',
                            transform: expandirNfe ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </Box>

                      <Collapse in={expandirNfe}>
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: theme.palette.secondary.main }}
                          >
                            Remessa: {formatCurrency(totais.valornferemessa)}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: theme.palette.warning.dark }}
                          >
                            Devolução: {formatCurrency(totais.valornfedevolucao)}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: theme.palette.error.dark }}
                          >
                            Cancelada: {formatCurrency(totais.valornfecancelada)}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.primary.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total NFC-e
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.warning.dark }}
                      >
                        {formatCurrency(totais.valornfce)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {totais.qtdenfce} notas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }}
                    >
                      <ReceiptLongIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.warning.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total Geral
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: 1, color: theme.palette.success.dark }}
                      >
                        {formatCurrency(totais.valortotal)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {totais.qtdetotal} notas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                      }}
                    >
                      <SummarizeIcon
                        sx={{
                          fontSize: 32,
                          color: theme.palette.success.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* IMPOSTOS — informativo; os valores acima são o valor total das notas (igual ao relatório) */}
            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                    Impostos das notas (informativo — os valores acima são o valor total das notas, igual ao Relatório NFE e à SEFAZ)
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={3}>
                    {[
                      { label: 'ICMS', valor: impostos.icms },
                      { label: 'ICMS-ST', valor: impostos.icmsst },
                      { label: 'IPI', valor: impostos.ipi },
                      { label: 'PIS', valor: impostos.pis },
                      { label: 'COFINS', valor: impostos.cofins },
                      { label: 'Total de impostos', valor: impostos.total, destaque: true },
                      { label: 'Descontos nos itens', valor: impostos.desconto },
                    ].map(({ label, valor, destaque }) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={destaque ? 'bold' : 600} sx={destaque ? { color: theme.palette.error.dark } : undefined}>
                          {formatCurrency(valor)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* GRAFICOS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} lg={9} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                  >
                    Evolução de Notas
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <ButtonGroup size="small" sx={graficoResp.grupoBotoes}>
                      <Button
                        variant={periodo === 'atual' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('atual')}
                      >
                        Atual
                      </Button>

                      <Button
                        variant={periodo === 'mensal' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('mensal')}
                      >
                        Mensal
                      </Button>

                      <Button
                        variant={periodo === 'anual' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('anual')}
                      >
                        Anual
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="small" sx={graficoResp.grupoBotoes}>
                      <Button
                        variant={modoExibicao === 'detalhado' ? 'contained' : 'outlined'}
                        onClick={() => setModoExibicao('detalhado')}
                      >
                        NF-e / NFC-e
                      </Button>

                      <Button
                        variant={modoExibicao === 'total' ? 'contained' : 'outlined'}
                        onClick={() => setModoExibicao('total')}
                      >
                        Total
                      </Button>

                      <Button
                        variant={modoExibicao === 'compravenda' ? 'contained' : 'outlined'}
                        onClick={() => setModoExibicao('compravenda')}
                      >
                        Compra / Venda
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: graficoResp.altura(360),
                    position: 'relative',
                  }}
                >
                  {isLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  )}

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <ComposedChart margin={graficoResp.margem} data={chartData}>
                      <defs>
                        <linearGradient
                          id="gradientTotalNotas"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.8}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>

                        <linearGradient
                          id="gradientVendasCV"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.7}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>

                        <linearGradient
                          id="gradientComprasCV"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.info.main}
                            stopOpacity={0.7}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.info.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid {...graficoResp.grade} strokeDasharray="3 3" />

                      <XAxis {...graficoResp.eixoX} dataKey="label" />

                      <YAxis {...graficoResp.eixoY}
                        tickFormatter={(value) =>
                          value.toLocaleString('pt-BR', {
                            notation: 'compact',
                          })
                        }
                      />

                      <RechartsTooltip {...graficoResp.dica} content={<CustomTooltip />} />

                      <Legend {...graficoResp.legenda} />

                      {modoExibicao === 'detalhado' && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="valornfe"
                            name="NF-e"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={{ r: 3, fill: theme.palette.primary.main }}
                            activeDot={{ r: 6 }}
                          />

                          <Line
                            type="monotone"
                            dataKey="valornfce"
                            name="NFC-e"
                            stroke={theme.palette.warning.main}
                            strokeWidth={2}
                            dot={{ r: 3, fill: theme.palette.warning.main }}
                            activeDot={{ r: 6 }}
                          />
                        </>
                      )}

                      {modoExibicao === 'total' && (
                        <Area
                          type="monotone"
                          dataKey="valortotal"
                          name="Total"
                          stroke={theme.palette.success.main}
                          fill="url(#gradientTotalNotas)"
                          strokeWidth={2}
                        />
                      )}

                      {modoExibicao === 'compravenda' && (
                        <>
                          <Area
                            type="monotone"
                            dataKey="valortotal"
                            name="Venda (NF)"
                            stroke={theme.palette.success.main}
                            fill="url(#gradientVendasCV)"
                            strokeWidth={2}
                          />

                          <Area
                            type="monotone"
                            dataKey="valorcompra"
                            name="Compra (NF)"
                            stroke={theme.palette.info.main}
                            fill="url(#gradientComprasCV)"
                            strokeWidth={2}
                          />
                        </>
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* GRAFICO ROSCA */}
            <Grid item xs={12} lg={3} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: 2,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                  >
                    {modoRosca === 'compravenda' ? 'Compras x Vendas' : 'NF-e x NFC-e'}
                  </Typography>

                  <ButtonGroup size="small" sx={graficoResp.grupoBotoes}>
                    <Button
                      variant={modoRosca === 'notas' ? 'contained' : 'outlined'}
                      onClick={() => setModoRosca('notas')}
                    >
                      NF-e / NFC-e
                    </Button>

                    <Button
                      variant={modoRosca === 'compravenda' ? 'contained' : 'outlined'}
                      onClick={() => setModoRosca('compravenda')}
                    >
                      Compra / Venda
                    </Button>
                  </ButtonGroup>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <PieChart>
                      <Pie {...graficoResp.pizza}
                        data={dadosRosca}
                        dataKey="valor"
                        innerRadius={35}
                        outerRadius={55}
                        paddingAngle={3}
                      >
                        {dadosRosca.map((item, index) => (
                          <Cell
                            key={index}
                            fill={item.color}
                          />
                        ))}
                      </Pie>

                      <RechartsTooltip {...graficoResp.dica}
                        formatter={(value: any, name: any, entry: any) => {
                          const qtd = entry?.payload?.value ?? 0;
                          const percentual = totalValorRosca > 0 ? ((Number(value) / totalValorRosca) * 100).toFixed(1) : '0';

                          return [
                            `${formatCurrency(Number(value))} · ${qtd} notas · ${percentual}%`,
                            name,
                          ];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box mt={1}>
                  {dadosRosca.map((item) => (
                    <Box
                      key={item.name}
                      sx={{ mb: 1.5 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: item.color,
                            }}
                          />

                          <Typography variant="caption">
                            {item.name}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: item.color }}
                        >
                          {totalValorRosca > 0 ? ((item.valor / totalValorRosca) * 100).toFixed(1) : '0'}%
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 2.25 }}
                      >
                        {item.value} notas · {formatCurrency(item.valor)}
                      </Typography>
                    </Box>
                  ))}

                  {totalValorRosca === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Nenhum dado encontrado
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* TOP PRODUTOS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <RankingProdutos
                titulo="Produtos em Notas (Quantidade)"
                icon={<Inventory2Icon sx={{ color: theme.palette.info.dark }} />}
                cor={theme.palette.info.main}
                itens={dados?.topProdutosQuantidade ?? []}
                metrica="quantidade"
              />
            </Grid>

            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <RankingProdutos
                titulo="Produtos em Notas (Valor)"
                icon={<TrendingUpIcon sx={{ color: theme.palette.secondary.main }} />}
                cor={theme.palette.secondary.main}
                itens={dados?.topProdutosValor ?? []}
                metrica="valor"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={() => setOpenMsg(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenMsg(false)} severity={msgType} sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>
    </LayoutBaseDeDashboard>
  );
};
